/**
 * Shared constants for talking to the Noeracle attestation service.
 * Endpoint lives at api.noeracle.org per CLAUDE.md / MEMORY (Fly app `noeracle`).
 */
export const API_BASE = "https://api.noeracle.org";

/** Matches scripts/keeper/config.mjs in noeracle/noeracle. */
export const ROUND_WINDOW_MS = 500;

export const ENDPOINTS = {
  health: `${API_BASE}/health`,
  latest: `${API_BASE}/v1/latest`,
  stream: `${API_BASE}/v1/stream`,
} as const;

export const ORACLE_CONTRACT_ID =
  "CAYIP67UDVX5UPXGN3XDAWVIEFBAVG6G7LUESEOU3NUQKTWN55W34YBG";

export const STELLAR_EXPERT = {
  contract: (id: string) =>
    `https://stellar.expert/explorer/testnet/contract/${id}`,
} as const;

/** /v1/latest response shape — what the keeper emits per asset. */
export type AssetEntry = {
  asset: string;
  /** Byte-tag form, e.g. "BTCUSD" (used inside the signed message). */
  tag: string;
  /** Integer price as a decimal string (i128). */
  price: string;
  /** Human price for display. */
  price_human: number;
  /** Unix seconds. */
  timestamp: number;
  round_id: number;
  /** Number of upstream exchanges aggregated this round. */
  sources: number;
  /** Ed25519 public key, hex. */
  publisher: string;
  /**
   * 40-byte signed message, hex (80 chars). Structure:
   *   - bytes  0-7:  asset tag (8 bytes, UTF-8 + null-padded)
   *   - bytes  8-23: price (i128 BE, 16 bytes)
   *   - bytes 24-31: timestamp (u64 BE, 8 bytes)
   *   - bytes 32-39: round_id (u64 BE, 8 bytes)
   */
  message: string;
  /** Ed25519 signature, hex (128 chars / 64 bytes). */
  signature: string;
};

export type LatestResponse = {
  assets: Record<string, AssetEntry>;
};

export type HealthResponse = {
  status: "ok" | "degraded" | "starting";
  uptime_s: number;
  last_signed_age_s: number | null;
  rounds_signed?: number;
};

/** Ordered list of assets we show on the hero & terminal. Primary first. */
export const HERO_ASSETS = [
  "BTC/USD",
  "ETH/USD",
  "SOL/USD",
  "XRP/USD",
  "ADA/USD",
  "XLM/USD",
  "PAXG/USD",
  "XAUT/USD",
  "USDC/USD",
  "BNB/USD",
  "TRX/USD",
  "HYPE/USD",
  "DOGE/USD",
  "PUMP/USD",
  "ZEC/USD",
  "LINK/USD",
  "UNI/USD",
  "BCH/USD",
  "LTC/USD",
] as const;

export type HeroAsset = (typeof HERO_ASSETS)[number];

/** Per-asset decimal places for display formatting. */
export const ASSET_DECIMALS: Record<string, number> = {
  "BTC/USD": 2,
  "ETH/USD": 2,
  "SOL/USD": 2,
  "XRP/USD": 4,
  "ADA/USD": 5,
  "XLM/USD": 5,
  "PAXG/USD": 2,
  "XAUT/USD": 2,
  "USDC/USD": 5,
  "BNB/USD": 2,
  "TRX/USD": 5,
  "HYPE/USD": 2,
  "DOGE/USD": 5,
  "PUMP/USD": 6,
  "ZEC/USD": 2,
  "LINK/USD": 3,
  "UNI/USD": 3,
  "BCH/USD": 2,
  "LTC/USD": 2,
};
